/**
 * Legal Document mutations using React Relay
 *
 * Provides the `publishLegalDocument` GraphQL mutation, used to publish a new
 * version of a legal document (e.g. a system's Terms of Service). This replaces
 * the legacy REST `PUT /system/tos` endpoint.
 *
 * Note: Run `npm run relay` after making changes to GraphQL mutations
 * to regenerate the type artifacts.
 */

import { useCallback } from 'react';
import { useMutation, commitMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useLegalDocumentMutationsPublishMutation } from './__generated__/useLegalDocumentMutationsPublishMutation.graphql';

// ============================================================================
// GraphQL Mutations
// ============================================================================

const PublishLegalDocumentMutation = graphql`
  mutation useLegalDocumentMutationsPublishMutation($input: PublishLegalDocumentInput!) {
    publishLegalDocument(input: $input) {
      errors {
        code
        fields
        message
        shortMessage
      }
      result {
        id
        documentType
        locale
        version
        systemId
        organizationId
        effectiveFrom
        textContent
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface MutationError {
  code?: string | null;
  fields?: readonly string[] | null;
  message?: string | null;
  shortMessage?: string | null;
}

export interface MutationResult<T> {
  data: T | null;
  errors: MutationError[];
  isSuccess: boolean;
}

export interface PublishLegalDocumentInput {
  documentType: string;
  effectiveFrom: string;
  locale: string;
  textContent: string;
  version: string;
  systemId?: string | null;
  organizationId?: string | null;
  storageUri?: string | null;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for publishing a legal document (e.g. Terms of Service).
 *
 * @example
 * const { publishLegalDocument, isLoading } = usePublishLegalDocumentMutation();
 *
 * const handleSave = async () => {
 *   const result = await publishLegalDocument({
 *     documentType: 'tos',
 *     locale: 'en',
 *     textContent: editorHtml,
 *     systemId: system.gid,
 *     version: new Date().toISOString(),
 *     effectiveFrom: new Date().toISOString(),
 *   });
 *   if (result.isSuccess) {
 *     toast.success('Terms of Service updated');
 *   }
 * };
 */
export function usePublishLegalDocumentMutation() {
  const [commit, isInFlight] = useMutation<useLegalDocumentMutationsPublishMutation>(
    PublishLegalDocumentMutation
  );

  const publishLegalDocument = useCallback(
    (
      input: PublishLegalDocumentInput
    ): Promise<
      MutationResult<
        useLegalDocumentMutationsPublishMutation['response']['publishLegalDocument']['result']
      >
    > => {
      return new Promise((resolve) => {
        commit({
          variables: { input },
          onCompleted: (response) => {
            const result = response.publishLegalDocument;
            resolve({
              data: result.result ?? null,
              errors: result.errors as MutationError[],
              isSuccess: result.errors.length === 0 && result.result !== null,
            });
          },
          onError: (error) => {
            resolve({
              data: null,
              errors: [{ message: error.message }],
              isSuccess: false,
            });
          },
        });
      });
    },
    [commit]
  );

  return { publishLegalDocument, isLoading: isInFlight };
}

// ============================================================================
// Imperative API (without hooks)
// ============================================================================

/**
 * Publish a legal document imperatively (without a hook).
 *
 * Useful when the call originates from a plain callback (e.g. wrapped in a
 * `toast.promise`) rather than inside a component's render.
 */
export function publishLegalDocumentMutation(
  input: PublishLegalDocumentInput
): Promise<
  MutationResult<
    useLegalDocumentMutationsPublishMutation['response']['publishLegalDocument']['result']
  >
> {
  return new Promise((resolve) => {
    commitMutation<useLegalDocumentMutationsPublishMutation>(relayEnvironment, {
      mutation: PublishLegalDocumentMutation,
      variables: { input },
      onCompleted: (response) => {
        const result = response.publishLegalDocument;
        resolve({
          data: result.result ?? null,
          errors: result.errors as MutationError[],
          isSuccess: result.errors.length === 0 && result.result !== null,
        });
      },
      onError: (error) => {
        resolve({
          data: null,
          errors: [{ message: error.message }],
          isSuccess: false,
        });
      },
    });
  });
}
